import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Truck, ArrowRight, Mail, Compass, Route, ShieldCheck, Target, Handshake, Heart } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { applyPageSeo } from "@/lib/seo";
import { STRATEGIST } from "@/data/strategist";
import StrategyCallDialog from "@/components/StrategyCallDialog";

const PAGE_URL = "https://routehaulpathways.com/about";
const OG_IMAGE = "https://static.prod-images.emergentagent.com/jobs/ad103537-b988-4363-adb5-ef152c7e8f33/images/301b9421443a9e85ceafb6619141b83a9740ff27d426983ff621bab4c5aeb512.jpeg";
const TITLE = `About ${STRATEGIST.name} — Lane Strategist for Owner-Operators | RouteHaul`;
const DESCRIPTION =
  `Meet ${STRATEGIST.name}, ${STRATEGIST.title} and founder of RouteHaul Logistics Pathways — mentorship that helps freight owner-operators pick profitable lanes, price loads above break-even, and build a trucking business that lasts.`;

const HELPS = [
  { icon: Route, title: "Profitable lane selection", text: "Choosing routes and regions by rate-per-mile, fuel cost, terrain and deadhead — so your miles make money instead of just keeping you busy." },
  { icon: Target, title: "Pricing above break-even", text: "Reading a load's true profitability and holding the line on rates, so you stop hauling cheap freight that quietly loses money after expenses." },
  { icon: Compass, title: "From driver to owner", text: "A clear, plain-English path through authority, compliance and cash flow — the business side that trips up most new carriers." },
];

const VALUES = [
  { icon: Heart, title: "Mentorship, not a course sale", text: "RouteHaul teaches you the business first and only then hands you tools. No hype, no get-rich pitch — just the real work of running profitable miles." },
  { icon: Handshake, title: "Built on real operating experience", text: "The guidance comes from actually running lanes and a carrier, not from repackaging someone else's course." },
  { icon: ShieldCheck, title: "Honest about the hard parts", text: "Cash reserves, slow-paying brokers, compliance deadlines — we tell you the parts most sellers skip, because that's what keeps new carriers alive." },
];

export default function About() {
  const { user } = useAuth();
  useEffect(() => applyPageSeo({
    title: TITLE,
    description: DESCRIPTION,
    canonical: PAGE_URL,
    image: OG_IMAGE,
    imageAlt: `${STRATEGIST.name}, Lane Strategist — RouteHaul Logistics Pathways`,
    seoKey: "about",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Organization",
          name: "RouteHaul Logistics Pathways",
          url: "https://routehaulpathways.com/",
          logo: OG_IMAGE,
          description: "Education and mentorship platform that helps freight owner-operators start and run a profitable trucking business.",
          founder: { "@type": "Person", name: STRATEGIST.name },
          email: STRATEGIST.email,
        },
        {
          "@type": "Person",
          name: STRATEGIST.name,
          jobTitle: STRATEGIST.title,
          description: STRATEGIST.blurb,
          email: STRATEGIST.email,
          url: PAGE_URL,
          worksFor: { "@type": "Organization", name: STRATEGIST.business },
          knowsAbout: ["Owner-operator trucking", "Freight lane strategy", "Load pricing", "Trucking authority", "Cost per mile"],
        },
      ],
    },
  }), []);

  return (
    <div className="min-h-screen bg-white" data-testid="about-page">
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between gap-4">
          <Link to="/" data-testid="about-brand" className="flex items-center gap-2 font-heading font-black text-lg tracking-tight text-slate-900">
            <Truck className="w-6 h-6 text-brand" strokeWidth={2.5} />
            <span>RouteHaul<span className="hidden sm:inline text-slate-400 font-bold"> Logistics Pathways</span></span>
          </Link>
          <div className="flex items-center gap-2 sm:gap-3">
            {user ? (
              <Link to="/" data-testid="about-dashboard"><Button className="h-10 px-4 sm:px-5 rounded-full bg-brand hover:bg-brand-hover text-white font-bold text-xs sm:text-sm tracking-wide uppercase">Go to dashboard</Button></Link>
            ) : (
              <>
                <Link to="/login" data-testid="about-login"><Button variant="ghost" className="h-10 px-4 rounded-full text-slate-700 font-bold text-xs sm:text-sm tracking-wide uppercase hover:bg-slate-100">Log in</Button></Link>
                <Link to="/register" data-testid="about-header-signup"><Button className="h-10 px-4 sm:px-5 rounded-full bg-brand hover:bg-brand-hover text-white font-bold text-xs sm:text-sm tracking-wide uppercase">Get started</Button></Link>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="border-b border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 py-16 sm:py-20 grid lg:grid-cols-5 gap-12 items-center">
          <div className="lg:col-span-2">
            <div className="relative">
              <div className="absolute -top-3 -left-3 w-20 h-20 bg-brand rounded-md z-0" />
              <div className="relative z-10 bg-slate-900 rounded-md p-8 text-white">
                <div className="w-16 h-16 rounded-full bg-brand flex items-center justify-center font-heading font-black text-2xl mb-5">LW</div>
                <h2 className="font-heading text-2xl font-black tracking-tight">{STRATEGIST.name}</h2>
                <p className="text-brand font-bold text-sm mt-1">{STRATEGIST.title}</p>
                <p className="text-slate-400 text-sm mt-0.5">{STRATEGIST.business}</p>
                <a href={`mailto:${STRATEGIST.email}`} data-testid="about-strategist-email" className="inline-flex items-center gap-1.5 text-sm text-slate-300 hover:text-brand mt-4 break-all">
                  <Mail className="w-4 h-4 shrink-0" /> {STRATEGIST.email}
                </a>
              </div>
            </div>
          </div>
          <div className="lg:col-span-3">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
              <div className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-brand mb-5">
                <span className="w-8 h-px bg-brand" /> Meet your lane strategist
              </div>
              <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter font-black text-slate-900 leading-[1.02]">
                Mentorship from someone who's actually done it.
              </h1>
              <p className="mt-5 text-base sm:text-lg leading-relaxed text-slate-700">{STRATEGIST.blurb}</p>
              <p className="mt-4 text-slate-600 leading-relaxed">
                RouteHaul Logistics Pathways exists for one reason: most new trucking businesses fail on the
                paperwork, compliance and cash flow — not the driving. {STRATEGIST.name.split(" ")[0]} built
                this platform to teach owner-operators the business side first, then hand them the tools to
                run the numbers and launch with confidence.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What LaRaine helps with */}
      <section className="max-w-5xl mx-auto px-6 py-16">
        <h2 className="font-heading text-3xl sm:text-4xl tracking-tighter font-black text-slate-900 mb-3">What {STRATEGIST.name.split(" ")[0]} helps with</h2>
        <p className="text-slate-600 mb-10 max-w-2xl">The difference between a busy truck and a profitable one usually comes down to three things:</p>
        <div className="grid md:grid-cols-3 gap-5">
          {HELPS.map((h, i) => (
            <motion.div
              key={h.title}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.3, delay: i * 0.06 }}
              data-testid={`about-help-${i}`}
              className="rounded-2xl border border-slate-200 p-7 hover:border-brand transition-colors"
            >
              <div className="w-12 h-12 rounded-xl bg-orange-50 flex items-center justify-center mb-5"><h.icon className="w-6 h-6 text-brand" strokeWidth={2.2} /></div>
              <h3 className="font-heading text-lg font-bold text-slate-900 mb-2">{h.title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">{h.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Values / how we work */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-5xl mx-auto px-6 py-16">
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tighter font-black text-slate-900 mb-10">How RouteHaul is different</h2>
          <div className="grid md:grid-cols-3 gap-5">
            {VALUES.map((v, i) => (
              <div key={v.title} data-testid={`about-value-${i}`} className="rounded-2xl border border-slate-200 bg-white p-7">
                <div className="w-12 h-12 rounded-xl bg-orange-50 flex items-center justify-center mb-5"><v.icon className="w-6 h-6 text-brand" strokeWidth={2.2} /></div>
                <h3 className="font-heading text-lg font-bold text-slate-900 mb-2">{v.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{v.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-brand text-white">
        <div className="max-w-3xl mx-auto px-6 py-16 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tighter font-black leading-tight">Ready to build a business, not just drive a truck?</h2>
          <p className="mt-4 text-orange-50 text-lg">Start free with the 12-Step Business Foundation Guide, or reach out to {STRATEGIST.name.split(" ")[0]} directly for a lane strategy session.</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center items-center">
            <Link to="/register" data-testid="about-final-cta">
              <Button className="h-14 px-8 rounded-full bg-white text-brand hover:bg-orange-50 text-base font-bold group w-full sm:w-auto">
                Start the free guide
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
            <StrategyCallDialog source="about_page" triggerClassName="h-14 px-8 rounded-full border border-white/60 bg-transparent text-white hover:bg-white/10 text-base font-bold w-full sm:w-auto" />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-8 text-sm text-slate-400 flex items-center justify-between flex-wrap gap-2">
          <span>© {new Date().getFullYear()} RouteHaul Logistics Pathways</span>
          <div className="flex items-center gap-4">
            <Link to="/" className="hover:text-slate-900">Home</Link>
            <Link to="/how-to-get-your-own-authority" className="hover:text-slate-900">Get Your Authority</Link>
            <Link to="/cost-per-mile-calculator" className="hover:text-slate-900">CPM Calculator</Link>
            <Link to="/owner-operator-faq" className="hover:text-slate-900">FAQ</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
